import cx from "classnames";
import * as React from "react";
import { useEffect, useRef } from "react";
import * as StateLib from "@/Utils/StateLib";
import { useBlocker } from "react-router-dom";
import { useAppNavigate } from "@/hooks/useAppNavigate";

import { SettingsModalAction } from "../SettingsModal";
import {
  Tabs,
  Text,
  Flex,
  Select,
  DialogContent,
  Icon,
  Button,
  Alert,
  isNullish
} from "@theorgchartdev/storybook";
import {
  ChartSettingSectionNames,
  ChartSettingsSectionRoutes,
  enumChartSettings,
  getChartSettingsTestPrefix,
  sectionsRequireRefresh
} from "@/providers/NavigationProvider";
import { SearchDirectoryConfiguration } from "./SearchDirectoryConfiguration/SearchDirectoryConfiguration";
import { ProfileConfiguration } from "./ProfileConfiguration/ProfileConfiguration";
import { MappingConfiguration } from "./MappingConfiguration/MappingConfiguration";
import { localizationPreSave } from "./MappingConfiguration/LocalizationCard";
import { SubchartConfiguration } from "./SubchartConfiguration/SubchartConfiguration";
import { FieldsFormulasConfiguration } from "./FieldsFormulasConfiguration/FieldsFormulasConfiguration";
import { PresentationConfiguration } from "./PresentationConfiguration/PresentationConfiguration";
import { RefreshConfiguration } from "./RefreshConfiguration/RefreshConfiguration";
import { DataSourceConfiguration } from "./DataSourceConfiguration/DataSourceConfiguration";
import { GetChartMetadata, LoadHeadersRFC, UpdateChartMetadata } from "@/Utils/Promises";
import { ImportFiltersConfiguration } from "./ImportFiltersConfiguration/ImportFiltersConfiguration";
import {
  AttachLoadedHeadersToChart,
  GetChartRefreshInfo,
  GetConnectionConfigured,
  GetNeedRefreshDiagnostics,
  LoadCurrentChartMapping,
  ValidateMapping
} from "./Utils";
import { PointInTimeModal } from "../../PointInTimeModal/PointInTimeModal";
import { GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { getFeatureAccess, IsRefreshable } from "@/Utils/Misc";
import { ImportTypes } from "@/Utils/Constants";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumBGOption,
  enumFeaturesList,
  enumIWizDataSet,
  enumObjClassNames
} from "@coreapp/constants";
import { QSortConfig } from "@coreapp/CoreApp/QSortConfig";
import { Globals } from "@coreapp/CoreApp/Globals";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { ValidateSortConfig } from "@coreapp/utils/ValidateSortConfig";
import { CapitalizeFirstWord } from "@/Utils/DataLib";
import { useToast } from "@/providers/ToastProvider";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { ChartDiagnostics } from "./ChartDiagnostics";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradePIT from "../../ChartExplorerModal/PointInTimeExplorer/UpgradePIT";
import UpgradeTabHeader from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeTabHeader";
import { contextType } from "focus-trap-react";
import { ForceRefreshModal } from "../ForceRefreshModal";
import { ForceRefreshFormulasModal } from "../ForceRefreshFormulasModal";
import { ChartTypePill } from "./ChartTypePill";
import { updateProfileState } from "./ProfileConfiguration/ProfileConfiguration.utils";

const ChartSettings = ({ chartID, sectionKey, currentTab, setCurrentTab, onCloseModal }) => {
  const nav = useAppNavigate();
  const {
    chartConfigRef,
    chartConfig,
    setChartConfig,
    clonedChartConfig,
    setClonedChartConfig,
    updateChartConfigRef,
    ReportEventLaunchChartSettings,
    showForceRefreshModal,
    setShowForceRefreshModal,
    showReloadFormulasModal,
    setShowReloadFormulasModal
  } = useChartSettingsContext();

  const validationRef = useRef(null);
  const userHideMappingAlert = useRef(false);

  const currentChartID = chartConfig?.["ID"] ?? null;

  const changedSectionsRef = useRef([]);
  const bypassDirtyCheckRef = useRef(false);

  const { addToast } = useToast();

  chartConfigRef.current = chartConfig;

  const showNeedChartRefreshAlert =
    chartConfigRef?.current?.sync?.diagnostics?.needsRefresh === true;

  const chartDiagnostics = new ChartDiagnostics(chartConfig);
  const showDataSourceFailuresAlert = chartDiagnostics.hasAnyFailures();

  const { showPricingAndPackaging, isSupportLogin } = usePermissionCheck();

  const wsMgr = GetWSMgr();

  const getChangedSections = () => {
    return changedSectionsRef?.current;
  };

  const setChangedSections = updatedSections => {
    changedSectionsRef.current = updatedSections;
  };

  useEffect(() => {
    let targetChartId = chartID;

    // If the chart ID is not specified in the URL, default to the open chart
    if (isNullish(targetChartId) || targetChartId === "undefined") {
      const doc = StateLib.GetDoc();
      targetChartId = doc?.GetChartID();
    }

    if (isNullish(targetChartId)) return;

    // If the target ID matches what is already in state, no need to reload
    if (chartConfigRef.current?.["ID"] === targetChartId) {
      return;
    }

    LoadChartMetadata(targetChartId);
    userHideMappingAlert.current = false;
  }, [chartID]);

  const isPositionDocument = React.useMemo(() => {
    if (!chartConfig) {
      return false;
    }

    return IsPositionChart(chartConfig);
  }, [chartConfig]);

  useEffect(() => {
    ReportEventLaunchChartSettings();
  }, []);

  useEffect(() => {
    const invalidMapping = showConfigureMappingButton();
    if (!invalidMapping) return;
    isFieldMapLoadAlertNeeded();
  }, [chartConfig]);

  const showConfigureMappingButton = () => {
    const validMapping = ValidateMapping(chartConfigRef.current);
    return !validMapping && GetConnectionConfigured(chartConfigRef.current);
  };

  const autoLoadMapping = async () => {
    await handleLoadFieldMapping({ importType: ImportTypes.import });
    bypassDirtyCheckRef.current = true;
    handleTabChange(1);
  };

  const isFieldMapLoadAlertNeeded = () => {
    if (userHideMappingAlert.current === false && showConfigureMappingButton()) {
      const wsMgr = GetWSMgr();
      wsMgr.CreateReactAlert(
        appstr("Load field mapping now"),
        appstr("Load field mapping info"),
        async () => {
          await autoLoadMapping();
          userHideMappingAlert.current = true;
          bypassDirtyCheckRef.current = false;
        },
        () => {
          userHideMappingAlert.current = true;
        },
        appstr("Load now"),
        appstr("Not now")
      );
    }
  };

  const LoadChartMetadata = async chartID => {
    // Chart Settings is the only surface that needs the unstripped data-source
    // config (connection credentials); request it explicitly. See GetChartMetadata.
    const { result } = await GetChartMetadata(chartID, true);
    const chartMetadata = result;
    updateChartConfigRef(chartMetadata);
    setClonedChartConfig(JSON.parse(JSON.stringify(chartMetadata)));
    const sectionRoute = ChartSettingsSectionRoutes[sectionKey];
    // URL canonicalization on mount (default section) — replace so Cancel
    // and browser back exit Settings instead of popping this normalized entry.
    nav.navigateSettings("chart", chartMetadata["ID"], sectionRoute, { replace: true });
  };

  const GetCurrentTabName = () => {
    return ChartSettingSectionNames[enumChartSettings[currentTab]];
  };

  const ResetChartConfig = (newSection, exit) => {
    updateChartConfigRef(clonedChartConfig);
    setChangedSections([]);
    if (newSection != null) {
      handleTabChange(newSection);
    }
    if (exit) onCloseModal();
  };

  const updateAppState = data => {
    const doc = StateLib.GetDoc();
    const wsMgr = StateLib.GetWSMgr();
    const iWizData = doc.GetIWizData();
    const changedSections = getChangedSections();
    let updateLayout = false;
    if (doc.GetChartID() != currentChartID) return;

    for (let aSection of changedSections) {
      switch (aSection) {
        case "maxQueryLimit":
          Globals._accountInfo.SetMaxRecords(data["maxQueryLimit"]);
          break;
        case "profile":
          let pm = doc.GetProfileMgr();
          if (pm) {
            updateProfileState(data, pm, doc);
          }
          break;
        case "views":
          let vm = doc.GetViewMgr();
          if (vm) {
            vm.ResetViewMgr();
            for (let aView of data["views"]) vm.AddView(aView);
          }
          break;
        case "mastertemplate":
          var isBackground = IsBackgroundFile(data["mastertemplate"]);
          let mpOption = isBackground ? enumBGOption.linked : enumBGOption.custom;
          iWizData.SetMasterOption(mpOption);
          iWizData.SetMasterFolder("Masters");
          iWizData.SetMasterFile(data["mastertemplate"]);
          if (!isBackground) {
            wsMgr?.fBackgroundMgr.ClearActiveBackground();
          }
          wsMgr.ApplyBackground(enumIWizDataSet.refresh, null, false);
          break;
        case "svrtemplate":
          //TODO: Do we actually do anything here?
          break;
        case "sort":
          var sortConfig = new QSortConfig().LoadFromJSON(data["sort"]);
          iWizData.SetSortCriteria(sortConfig);
          const scm = doc.GetSubChartMgr();
          var parentList = scm.GetParentListForChart();
          if (sortConfig != null) {
            sortConfig = ValidateSortConfig(sortConfig, doc);
            doc.PerformSort(sortConfig, parentList, false);
            updateLayout = true;
          }
          break;
        case "keys":
          doc.SetSearchKeys(data["keys"]);
          break;
        case "excel":
          doc.SetExcelKeys(data.excel?.fields ?? []);
          break;
        case "directory":
          if (!doc.fDirectoryInfo) doc.fDirectoryInfo = {};
          doc.fDirectoryInfo = { ...doc.fDirectoryInfo, ...data["directory"] };
          break;
        case "localization":
          Globals._svrChartMgr.SetLocaleInfo(data["localizationConfig"]);
          break;
        case "highlights":
          var highlightMgr = doc.GetHighlightMgr();
          highlightMgr.ResetHighlightMgr();
          var o = doc.GetObjDB();
          var aClass = o.GetClassByName("Box");
          Globals._svrChartMgr.fActiveHighlights = null;
          doc.AddDynFilters(aClass, { highlights: data["highlights"] }, wsMgr);
          wsMgr.ApplyChartHighlights();
          ReRenderCanvas();
          break;
        case "filters":
          var filterMgr = doc.GetFilterMgr();
          filterMgr.ResetFilterMgr();
          var o = doc.GetObjDB();
          var aClass = o.GetClassByName("Box");
          Globals._svrChartMgr.fActiveFilters = null;
          doc.AddDynFilters(aClass, { filters: data["filters"] }, wsMgr);
          wsMgr.ApplyChartFilters();
          ReRenderCanvas();
          break;
        case "fieldFormats":
        case "formulas":
        case "pointintime":
          break;
      }
    }

    doc.UpdateDocContext();

    if (updateLayout) {
      wsMgr.RecalcChartLayout();
      wsMgr.ReloadCurrentSubChart();
    }
  };

  const changesRequireRefresh = () => {
    const changedSections = getChangedSections();
    return changedSections.some(name => sectionsRequireRefresh.includes(name));
  };

  const SaveChartConfigChanges = async (data, newSection, exit, postSaveFn) => {
    let configChanges = { ...data };
    if (configChanges.localizationConfig) {
      configChanges = localizationPreSave(configChanges);
    }
    const { success } = await UpdateChartMetadata(currentChartID, JSON.stringify(configChanges));

    if (success) {
      const refreshRequired = changesRequireRefresh();
      const nextData = { ...chartConfigRef.current, ...configChanges };
      setClonedChartConfig(JSON.parse(JSON.stringify(nextData)));
      updateChartConfigRef(nextData);
      updateAppState(configChanges);
      setChangedSections([]); // Is changedSections an array or a string?
      if (newSection != null) {
        handleTabChange(newSection);
      }
      addToast({ message: appstr("Changes have been saved"), status: "success" });
      if (refreshRequired) {
        addToast({ message: appstr("Refresh chart notice"), status: "warning" });
      }
      if (exit) onCloseModal();
      if (postSaveFn) {
        postSaveFn();
      }
    } else {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    }
  };

  const GetChangedConfigData = () => {
    const changedSections = getChangedSections();
    let data = {};
    for (let section of changedSections) {
      data[section] = chartConfigRef.current[section];
      //  TODO do not follow this pattern. Need to revert and replace this
      if (section === "coHeads") {
        data[section] = data[section].filter(item => item.PersonID !== "");
      }
    }
    return data;
  };

  const SaveSettings = (props = {}) => {
    const { newSection, exit, onPostPrompt, onDismiss, additionalData, postSaveFn } = props;
    // save guard against bad state
    if (currentChartID != chartConfigRef.current["ID"]) return;
    let answer = { proceed: true, alertmsg: "", headermsg: "", alertAccept: appstr("OK") };
    if (validationRef?.current?.DoValidate) {
      answer = validationRef.current.DoValidate();
    }

    if (!answer.proceed) {
      const wsMgr = StateLib.GetWSMgr();
      var header = answer.headermsg;
      var message = answer.alertmsg;
      wsMgr.CreateReactAlert(
        header,
        message,
        () => {
          onPostPrompt?.();
        },
        undefined,
        answer.alertAccept,
        undefined,
        onDismiss
      );
      return;
    }
    let diagnostics = {};
    let data = GetChangedConfigData();
    if (changesRequireRefresh()) {
      diagnostics = GetNeedRefreshDiagnostics(chartConfigRef.current);
    }
    SaveChartConfigChanges(
      { ...data, ...additionalData, ...diagnostics },
      newSection,
      exit,
      postSaveFn
    );
  };

  const UpdateChartConfig = (updatedChartConfig, key) => {
    const nextChartConfig = { ...chartConfigRef.current, ...updatedChartConfig };
    updateChartConfigRef(nextChartConfig);
    UpdateChangedSections(key);
  };

  // can be array of strings for multiple updated sections or 1 string key for single section
  const UpdateChangedSections = keys => {
    const changedSections = getChangedSections();
    if (Array.isArray(keys)) {
      let newKeys = [];
      for (let k of keys) {
        if (!changedSections.includes(k)) newKeys.push(k);
      }
      if (newKeys.length > 0) setChangedSections([...changedSections, ...newKeys]);
    } else {
      if (keys && keys.length > 0 && !changedSections.includes(keys))
        setChangedSections([...changedSections, keys]);
    }
  };

  const PromptToSave = (changeSection, exit, onPostPrompt, onDismiss) => {
    const wsMgr = StateLib.GetWSMgr();
    let answer = { proceed: true, alertmsg: "", headermsg: "", alertAccept: "" };
    if (validationRef?.current?.DoValidate) {
      answer = validationRef.current.DoValidate();
    }

    if (!answer.proceed) {
      const wsMgr = StateLib.GetWSMgr();
      var header = answer.headermsg;
      var message = answer.alertmsg;
      wsMgr.CreateReactAlert(
        header,
        message,
        () => {
          onDismiss?.();
        },
        () => {
          ResetChartConfig(changeSection, exit);
          onPostPrompt?.();
        },
        answer.alertAccept,
        appstr("Discard"),
        onDismiss
      );
    } else {
      var header = appstr("Save Changes");
      var message =
        appstr("saveconfirm1") + " " + appstr(GetCurrentTabName()) + appstr("saveconfirm2");
      wsMgr.CreateReactAlert(
        header,
        message,
        () => {
          SaveSettings({ changeSection, exit, postSaveFn: onPostPrompt, onDismiss });
        },
        () => {
          ResetChartConfig(changeSection, exit);
          onPostPrompt?.();
        },
        header,
        appstr("Discard"),
        onDismiss
      );
    }
  };

  const IsDirty = () => {
    const changedSections = getChangedSections();
    return changedSections.length > 0;
  };

  const checkBlock = () => {
    if (bypassDirtyCheckRef.current) {
      bypassDirtyCheckRef.current = true;
      return false;
    }

    return IsDirty();
  };

  const blocker = useBlocker(checkBlock);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }

    PromptToSave(null, false, blocker.proceed, blocker.reset);
  }, [blocker.state]);

  useEffect(() => {
    return () => {
      setChartConfig(null);
      if (chartConfigRef.current) {
        chartConfigRef.current = null;
      }
    };
  }, []);

  const ChangeChart = target => {
    let newChartID = target.value;
    if (newChartID == currentChartID) return;

    const sectionRoute = ChartSettingsSectionRoutes[sectionKey];
    nav.navigateSettings("chart", newChartID, sectionRoute);
  };

  const handleLoadFieldMapping = async ({
    importType = ImportTypes.import,
    advancedOptions = {}
  }) => {
    const options = {};
    options.type = importType;
    options.connectionOptions = chartConfigRef.current?.import;
    options.options = advancedOptions;

    const loadComplete = AutoLoadHeaders();

    if (!loadComplete) {
      const { response } = await LoadHeadersRFC(chartConfigRef.current, options);
      if (!response["success"]) return;
      const { newChartObj, changedSections } = AttachLoadedHeadersToChart(
        response,
        chartConfigRef.current,
        importType,
        null,
        null
      );
      const nextConfig = { ...chartConfigRef.current, ...newChartObj };
      UpdateChangedSections(changedSections);
      updateChartConfigRef(nextConfig);
    }
  };

  const AutoLoadHeaders = () => {
    const { newChartConfig, needRFC, changedSections } = LoadCurrentChartMapping(
      chartConfigRef.current
    );

    if (needRFC) return false;

    const nextConfig = { ...chartConfigRef.current, ...newChartConfig };
    UpdateChangedSections(changedSections);
    updateChartConfigRef(nextConfig);
    return true;
  };

  const handleResetFieldMapping = () => {
    let updateConfig = { fields: null };
    if (IsPositionChart(chartConfigRef.current)) {
      updateConfig.positionFields = null;
      updateConfig.holderFields = null;
    }
    UpdateChartConfig(updateConfig, Object.keys(updateConfig));
  };

  const handleTabChange = currentTabIndex => {
    setCurrentTab(currentTabIndex);
  };

  const GetTabContent = section => {
    let key = ChartSettingSectionNames[section];
    let sectionTab = {};
    sectionTab.header = CapitalizeFirstWord(appstr(key));
    sectionTab.dataTestId = getChartSettingsTestPrefix(section);
    let disabled = false;
    let content = null;
    switch (key) {
      case ChartSettingSectionNames.DataSource:
        content = (
          <DataSourceConfiguration
            chartConfig={chartConfig}
            handleUpdate={UpdateChartConfig}
            onSave={SaveSettings}
          />
        );
        break;
      case ChartSettingSectionNames.SearchAndDirectory:
        content = (
          <SearchDirectoryConfiguration
            chartConfig={chartConfig}
            handleUpdate={UpdateChartConfig}
          />
        );
        if (chartID) {
          if (chartConfig?.Timestamp === null && JSON.stringify(chartConfig?.directory) === "{}")
            disabled = true;
        }
        break;
      case ChartSettingSectionNames.Profile:
        content = (
          <ProfileConfiguration chartConfig={chartConfig} handleUpdate={UpdateChartConfig} />
        );
        if (chartID) {
          if (chartConfig?.Timestamp === null && chartConfig?.profile === null) disabled = true;
        }
        break;
      case ChartSettingSectionNames.Mapping:
        content = (
          <MappingConfiguration
            chartConfig={chartConfig}
            handleUpdate={UpdateChartConfig}
            handleLoadFieldMapping={handleLoadFieldMapping}
            handleResetFieldMapping={handleResetFieldMapping}
          />
        );
        break;
      case ChartSettingSectionNames.Presentation:
        content = (
          <PresentationConfiguration chartConfig={chartConfig} handleUpdate={UpdateChartConfig} />
        );
        break;
      case ChartSettingSectionNames.ImportFilters:
        content = (
          <ImportFiltersConfiguration chartConfig={chartConfig} handleUpdate={UpdateChartConfig} />
        );
        break;
      case ChartSettingSectionNames.Subcharts:
        content = (
          <SubchartConfiguration chartConfig={chartConfig} handleUpdate={UpdateChartConfig} />
        );
        break;
      case ChartSettingSectionNames.FieldsAndFormulas:
        content = (
          <FieldsFormulasConfiguration chartConfig={chartConfig} handleUpdate={UpdateChartConfig} />
        );

        break;
      case ChartSettingSectionNames.Refresh:
        content = (
          <RefreshConfiguration
            chartConfig={chartConfig}
            handleUpdate={UpdateChartConfig}
            validationRef={validationRef}
          />
        );

        break;
      case ChartSettingSectionNames.PointInTime:
        if (getFeatureAccess(enumFeaturesList.PIT) || !showPricingAndPackaging) {
          content = (
            <PointInTimeModal
              onUpdate={UpdateChartConfig}
              contentOnly={true}
              chartConfig={chartConfig}
              testIdPrefix={getChartSettingsTestPrefix(ChartSettingsSectionRoutes.PointInTime)}
            />
          );
          if (chartID) {
            if (
              !getFeatureAccess(enumFeaturesList.PIT) ||
              (chartConfig?.Timestamp === null && chartConfig?.pointintime === null)
            )
              disabled = true;
          }
        } else if (showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.PIT)) {
          content = <UpgradePIT />;
          sectionTab.header = (
            <UpgradeTabHeader
              label={appstr("Point In Time")}
              dataTestIdPrefix={sectionTab.dataTestId}
            />
          );
        }
        break;
      default:
        content = (
          <Text size={"medium"} modifier={"medium"} colorModifier={"grey"}>
            {appstr("Area under construction")}
          </Text>
        );
        break;
    }
    sectionTab.content = (
      <div className={cx("ocn-settings-content", "ocn-list-scrollbar")}>{content}</div>
    );
    sectionTab.disabled = disabled;
    return sectionTab;
  };
  const GetTabs = () => {
    let tabList = [];
    for (let section in ChartSettingSectionNames) {
      // The Point In Time section renders its own upsell when unentitled; nothing to suppress.
      tabList.push(GetTabContent(section));
    }
    return tabList;
  };

  const currentTabSlug =
    ChartSettingsSectionRoutes[Object.keys(ChartSettingSectionNames)[parseInt(currentTab)]];
  const currentTabTestPrefix = getChartSettingsTestPrefix(currentTabSlug);

  let saveButtonProps = {
    onClick: () => SaveSettings(),
    disabled: !IsDirty(),
    dataTestId: `${currentTabTestPrefix}-save-button`
  };

  let resetButtonProps = {
    onClick: () => ResetChartConfig(),
    disabled: !IsDirty(),
    dataTestId: `${currentTabTestPrefix}-reset-button`
  };

  let cancelButtonProps = {
    onClick: onCloseModal,
    dataTestId: `${currentTabTestPrefix}-cancel-button`
  };

  let forceRequeueButtonProps = isSupportLogin
    ? {
        onClick: () => {
          setShowForceRefreshModal(true);
        }
      }
    : null;

  let reloadFormulasButtonProps = isSupportLogin
    ? {
        onClick: () => {
          setShowReloadFormulasModal(true);
        }
      }
    : null;

  const IsCustomChartOpen = () => {
    const curDoc = wsMgr.GetDoc();
    return !curDoc?.IsServerChart();
  };

  let refreshButtonProps = {
    disabled:
      IsDirty() ||
      IsCustomChartOpen() ||
      !IsRefreshable(chartConfig) ||
      !getFeatureAccess(enumFeaturesList.refresh),
    dataTestId: `${currentTabTestPrefix}-refresh-button`,
    onClick: () => {
      const callbackObject = {
        HandleResponse: success => {
          // re-read the full config from the flagged fetch
          if (success) LoadChartMetadata(currentChartID);
        }
      };
      const refreshRFC = wsMgr.RefreshBaseFromSource(callbackObject, chartConfig.ID);
      refreshRFC.fReactCallback = true;
    }
  };

  const diagReport = GetChartErrorDiagnostics(chartConfig);
  const hasDiag = diagReport["success"] !== null;
  const showRefreshIcon = hasDiag;
  const hadIssues = hasDiag && diagReport["success"] === false;

  const getRefreshStatus = () => {
    let icon = null;
    let iconColor = null;
    if (hasDiag) {
      if (hadIssues) {
        icon = "circle-xmark";
        iconColor = "error";
      } else if (!isNullish(chartConfig["Timestamp"])) {
        icon = "circle-check";
        iconColor = "success";
      } else {
        icon = "circle-question";
        iconColor = "control";
      }
    }
    return { icon, iconColor };
  };

  const { icon, iconColor } = getRefreshStatus();
  const dateFormat = wsMgr.GetOrgInfo().GetDateFormat();
  const timeFormat = wsMgr.GetOrgInfo().GetTimeFormat();

  // GetConnectionConfigured is not a great name for this function -
  // it seems like it should be the opposite.
  const showMappingButton = showConfigureMappingButton();

  if (!chartConfig) return null;

  return (
    <React.Fragment>
      <Flex direction={"column"} height={"100%"}>
        <Flex padding={"2px 32px 16px 32px"} gap={"12px"} alignItems={"flex-start"} width={"100%"}>
          <Flex width={"300px"} direction={"column"} gap={"12px"} margin={"0 0 12px 0"}>
            <Select
              dataTestId={"chart-settings-chart-select"}
              options={BuildChartOptions(chartConfig, ChangeChart)}
              listWidth={"medium"}
              selectSize={"medium"}
              optionSize={"medium"}
              label={chartConfig?.name}
              value={chartConfig?.ID}
              rightContent={<ChartTypePill isPositionDocument={isPositionDocument} />}
            />
            <Flex gap={"4px"} alignItems={"center"}>
              {showRefreshIcon && icon && <Icon name={icon} size={"small"} modifier={iconColor} />}
              {chartConfig && (
                <Text className="ocn-settings-text-tertiary" size={"small"}>
                  {GetChartRefreshInfo(chartConfig, dateFormat, timeFormat)}
                </Text>
              )}
            </Flex>
          </Flex>
          {showMappingButton && (
            <Flex gap={"4px"} alignItems={"center"}>
              <Button
                dataTestId={`${DATA_SOURCES_TEST_PREFIX}-configure-field-mapping`}
                modifier={"secondary"}
                className={"oc-special-secondary"}
                size={"medium"}
                onClick={() => handleTabChange(1)}
              >
                {appstr("Configure field mapping")}
              </Button>
            </Flex>
          )}
          {showNeedChartRefreshAlert && !showMappingButton && (
            <Flex width={"fit-content"} direction={"column"} gap={"12px"} margin={"0 0 12px 0"}>
              <Alert severity={"warning"} withBorder showIcon>
                {appstr("Changes will be applied next data refresh")}
              </Alert>
            </Flex>
          )}
        </Flex>
        {showDataSourceFailuresAlert && (
          <Flex direction={"column"} gap={"12px"} padding={"2px 32px 16px 32px"}>
            <Alert
              severity={"warning"}
              withBorder
              showIcon
              className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
            >
              <div>
                <Text modifier={"bold"} size={"small"} colorModifier={"warning"}>
                  {`${appstr("Data source refresh failures")} (${chartDiagnostics.getFailuresCount()})`}
                </Text>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px" }}>
                  {chartDiagnostics.getFailuresList().map((failure, index) => (
                    <li key={index}>
                      <Text size={"small"} colorModifier={"warning"}>
                        {failure.displayText}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            </Alert>
          </Flex>
        )}
        <DialogContent customContentClass={"ocn-settings-container"}>
          <div className={"ocn-settings-content-container"}>
            <Tabs
              tabIndex={parseInt(currentTab)}
              customClass={"ocn-settings-tabs"}
              customContentClass={"ocn-settings-tabs-content"}
              tabs={GetTabs()}
              onTabChange={handleTabChange}
            />
          </div>
        </DialogContent>
        <SettingsModalAction
          saveButtonProps={saveButtonProps}
          resetButtonProps={resetButtonProps}
          cancelButtonProps={cancelButtonProps}
          refreshButtonProps={refreshButtonProps}
          forceRequeueButtonProps={forceRequeueButtonProps}
          reloadFormulasButtonProps={reloadFormulasButtonProps}
        />
      </Flex>
      {isSupportLogin && showForceRefreshModal && (
        <ForceRefreshModal
          showForceRefreshModal={showForceRefreshModal}
          setShowForceRefreshModal={setShowForceRefreshModal}
          handleUpdate={UpdateChartConfig}
          chartConfig={chartConfig}
        />
      )}
      {isSupportLogin && showReloadFormulasModal && (
        <ForceRefreshFormulasModal
          showReloadFormulasModal={showReloadFormulasModal}
          setShowReloadFormulasModal={setShowReloadFormulasModal}
          handleUpdate={UpdateChartConfig}
          chartConfig={chartConfig}
        />
      )}
    </React.Fragment>
  );
};

// Utils

const IsDataSourceFailure = chart => {
  var chartDiagSuccess = GetChartErrorDiagnostics(chart)["success"];
  return chartDiagSuccess === false;
};

const GetChartErrorDiagnostics = chart => {
  var diagReport = { success: null, diagnostics: null };
  if (!chart) return diagReport;

  var dataSourceFailure = diagReport["success"];
  var syncInfo = chart["sync"];
  if (!syncInfo || (syncInfo && !syncInfo["diagnostics"])) return diagReport;

  for (var aSection in syncInfo["diagnostics"]) {
    var targetSection = syncInfo["diagnostics"][aSection];
    if (Array.isArray(targetSection)) {
      var sections = targetSection.map(function (item) {
        return item ? item.success : null;
      });
      dataSourceFailure = sections.indexOf(false) > -1;
      if (sections.indexOf(null) > -1) dataSourceFailure = null;
    } else {
      dataSourceFailure = targetSection["success"] === false;
    }
    if (dataSourceFailure) break;
  }

  return { success: !dataSourceFailure, diagnostics: targetSection };
};

const BuildChartOptions = (chartMetadata, clickHandler) => {
  let optionList = [];

  let wsMgr = StateLib.GetWSMgr();
  var allCharts = wsMgr?.fOrgInfo.GetMasterChartList();

  if (!allCharts || !chartMetadata) return optionList;

  for (let chart of allCharts) {
    let option = {};
    let chartName = chart["name"];
    let chartID = chart["ID"];
    let defaultChart = false; //chartMetadata['defaultchart'] === chartID; // TODO not passed back anymore on login or attached to chart meta obj
    let chartHasIssue = IsDataSourceFailure(chart);
    let icon = null;

    option.label = chartName;
    option.value = chartID;
    option.dataTestId = `chart-settings-chart-option-${chartName}`;
    option.clickHandler = clickHandler;

    if (defaultChart) {
      icon = "star-regular";
    }
    if (chartHasIssue) {
      icon = "triangle-exclamation";
    }

    if (icon) {
      option.icon = icon;
    }

    optionList.push(option);
  }
  return optionList;
};

export default ChartSettings;
